<template>
  <div v-show="visible" class="modal-overlay">
    <div class="modal-content">
      <button class="close-button" @click="closeModal"><i class="bi bi-x"></i>{{ $t('streamingVideo.key030') }}</button>

      <div class="modal-price">
        <span>NT$ {{ price }}</span>
      </div>
      <div class="modal-message">
        <div id="nwp-payment-once" class="modal-iframe"></div>

        <div class="agreement-section">
          <div class="legal">
            <input type="checkbox" id="terms" v-model="agreed" />
            <label for="terms">
              {{ $t('shopping-cart.key018') }}
            </label>
          </div>
          <p v-html="formattedMessage" class="legal-text"></p>
        </div>

        <p v-if="!isPaid" @click="startPayment" class="pay_btn" :class="{ disabled: !agreed }">🔗 {{ $t('streamingVideo.key028') }}</p>
        <p>{{ $t('streamingVideo.key029') }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable no-useless-escape */
import { getInframePaymentInfo } from '@/api/cartService';
import { computed, defineProps, defineEmits, watch, ref } from 'vue';
import { showCustomAlert } from '@/utils/alertController';
import { useI18n } from 'vue-i18n';
import { HttpStatusCode } from 'axios';
import { useStore } from 'vuex';
import { CUSTOM_ERROR_CODE } from '@/utils/validation.js';

const env = process.env.NEWEB_PAY_ENV;
const hash = process.env.NEWEB_PAY_HASH;

// 定義組件接收的 props
const props = defineProps({
  visible: {
    type: Boolean,
    required: true,
  },
  onClose: {
    type: Function,
    required: false,
  },
  orderId: {
    type: String,
    default: '',
  },
  price: {
    type: Number,
    default: 0,
  },
});

const isPaid = ref(false);
const agreed = ref(false);
const isWaitingPayment = ref(false);
const store = useStore();
const currentLanguage = computed(() => store.state.currentLanguage);

const { t: $t } = useI18n();

// 定義事件
const emit = defineEmits(['update:visible', 'set-loading']);

const formattedMessage = computed(() => {
  return $t('shopping-cart.key041', {
    userPolicyLink: `<a href="#/${currentLanguage.value}/legal/user-policy" target="_blank">${$t('register.key025')}</a>`,
    privacyPolicyLink: `<a href="#/${currentLanguage.value}/legal/privacy" target="_blank">${$t('register.key026')}</a>`,
  });
});

// 關閉模態函數
const closeModal = () => {
  emit('update:visible', false);
  isPaid.value = false;
  agreed.value = false;
  if (typeof props.onClose === 'function') {
    props.onClose();
  }
};

const visible = computed(() => props.visible);

const setNewebPaySDK = () => {
  const language = currentLanguage.value === 'zh_tw' ? 'zhTW' : 'en';

  if (window.NewebPaySDK) {
    const customStyle = {
      inputStyle: {
        fontSize: '14px',
        fontWeight: '500',
        lineHeight: '1.5',
        color: 'black',
      },
      titleStyle: {
        style: {
          color: 'red',
        },
      },
      backgroundColor: '#fff',
      minHeight: '400',
      locale: language,
    };

    window.NewebPaySDK.setCardOncePay(env, customStyle);
  } else {
    console.error('NewebPaySDK 尚未載入');
  }
};

watch(visible, (newVal) => {
  if (newVal) {
    // neweb pay start
    setNewebPaySDK();
  } else {
    // remove child of iframe
    const iframe = document.getElementById('nwp-payment-once');
    if (iframe) {
      iframe.innerHTML = '';
    }
  }
});

// 開始幕後交易
const startPayment = async () => {
  if (!agreed.value) {
    showCustomAlert({ title: 'common.alert_key003', message: 'shopping-cart.key038' });
    return;
  }

  // 開啟彈出視窗進行 3D 驗證
  // 需先開啟 popup (before await), 避免被瀏覽器機制擋住, 導致無法開啟 3D 驗證頁面
  const popup = window.open('', 'payment3DVerification', 'width=600,height=700,scrollbars=yes,resizable=yes,status=yes,location=yes,toolbar=no,menubar=no');

  if (!popup) {
    showCustomAlert({ title: 'common.alert_key003', message: 'streamingVideo.key035' });
    return;
  }

  emit('set-loading', true);
  try {
    const result = await window.NewebPaySDK.getPrime(env, hash);

    if (result.responseCode == '0000') {
      isPaid.value = true;
      const prime = result.prime;

      let inframeResult;
      try {
        inframeResult = await getInframePaymentInfo(props.orderId, prime);
      } catch (error) {
        // close popup
        popup.close();
        if (error?.response?.status === HttpStatusCode.InternalServerError && error?.response.data?.data?.customCode === CUSTOM_ERROR_CODE.INFRAME_PAYMENT_SYSTEM_ERROR) {
          // TODO: I18n
          showCustomAlert({ title: 'common.alert_key003', message: '系統繁忙，請稍後再試' });
        } else {
          showCustomAlert({ title: 'common.alert_key003', message: 'streamingVideo.key036' });
        }
        closeModal();
        throw error;
      }
      const container = document.getElementById('nwp-payment-once');

      if (inframeResult.data.data.html && container) {
        // 顯示等待付款確認訊息
        isWaitingPayment.value = true;
        container.innerHTML = `
          <div style="display: flex; flex-direction: column; justify-content: center; align-items: center; height: 100%; background: white; border-radius: 6px;">
            <div style="font-size: 18px; color: #333; margin-bottom: 20px;">等待付款確認中...</div>
            <div style="width: 40px; height: 40px; border: 3px solid #f3f3f3; border-top: 3px solid #3498db; border-radius: 50%; animation: spin 1s linear infinite;"></div>
            <style>
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            </style>
            <div style="font-size: 14px; color: #666; margin-top: 15px; text-align: center; line-height: 1.4;">
              請在新開啟的視窗中完成 3D 驗證<br>
              驗證完成後此視窗將自動更新
            </div>
          </div>
        `;

        // 將 3D 驗證頁面寫入彈出視窗
        popup.document.open();
        popup.document.write(inframeResult.data.data.html);
        popup.document.close();

        // 監聽彈出視窗關閉事件
        const checkClosed = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkClosed);
            handlePaymentComplete();
          }
        }, 1000);

        // 設定超時處理（15分鐘後自動處理）
        setTimeout(() => {
          if (!popup.closed) {
            clearInterval(checkClosed);
            popup.close();
            handlePaymentTimeout();
          }
        }, 15 * 60 * 1000);
      } else {
        popup.close();
        showCustomAlert({ title: 'common.alert_key003', message: 'streamingVideo.key037' });
      }
    } else {
      popup.close();
      alert(result.resMessage);
    }
  } catch (error) {
    console.error(error);
    isPaid.value = false;
    isWaitingPayment.value = false;
  } finally {
    emit('set-loading', false);
  }
};

// 處理支付完成
const handlePaymentComplete = async () => {
  const container = document.getElementById('nwp-payment-once');
  if (container) {
    container.innerHTML = `
      <div style="display: flex; flex-direction: column; justify-content: center; align-items: center; height: 100%; background: white; border-radius: 6px;">
        <div style="font-size: 18px; color: #28a745; margin-bottom: 15px;">✓ 驗證完成</div>
        <div style="font-size: 14px; color: #666; text-align: center;">
          正在確認付款結果...
        </div>
      </div>
    `;
  }
  setTimeout(() => {
    // 關閉 modal 或顯示支付結果
    closeModal();
    // 或者顯示支付成功/失敗的訊息
  }, 2000);
};

// 處理支付超時
const handlePaymentTimeout = () => {
  const container = document.getElementById('nwp-payment-once');
  if (container) {
    container.innerHTML = `
      <div style="display: flex; flex-direction: column; justify-content: center; align-items: center; height: 100%; background: white; border-radius: 6px;">
        <div style="font-size: 18px; color: #dc3545; margin-bottom: 15px;">⚠ 驗證超時</div>
        <div style="font-size: 14px; color: #666; text-align: center; line-height: 1.4;">
          3D 驗證超時，請重新嘗試支付<br>
          或聯繫客服協助處理
        </div>
        <button onclick="location.reload()" style="margin-top: 15px; padding: 8px 16px; background: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer;">
          重新嘗試
        </button>
      </div>
    `;
  }
  isWaitingPayment.value = false;
  isPaid.value = false;
};
</script>

<style scoped>
.pay_btn {
  color: #000;
  background-color: #e79fff;
  border-radius: 5px;
  padding: 5px 10px;
  display: inline;
  cursor: pointer;
}
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 7777;
}

.modal-content {
  width: 600px;
  height: auto;
  max-height: 80vh;
  margin-top: 0px;
  background-color: rgba(16, 16, 16, 0.8);
  border: 1px solid #fff;
  position: relative;
  border-radius: 12px;
  overflow-y: auto;
  overflow-x: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
}

.modal-price {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: transparent;
  color: #d7263d;
  font-size: 24px;
  font-weight: 700;
  padding: 24px 0 8px 0;
  border-bottom: none;
  z-index: 2;
  position: relative;
}

.modal-price span {
  display: inline-block;
}

.modal-iframe {
  position: relative;
  height: 400px;
  width: 95%;
  min-width: 350px;
  min-height: 280px;
  border: 2px solid rgba(215, 38, 61, 0.3);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(4px);
  overflow: hidden;
  transition: all 0.3s ease;
  margin: 10px 0;
}

.close-button {
  position: absolute;
  top: 10px;
  right: 10px;
  font-size: 16px;
  font-weight: 400;
  background: #ff5f5f;
  border: none;
  color: white;
  padding: 5px 10px;
  cursor: pointer;
  border-radius: 4px;
  z-index: 10;
}
.close-button:hover {
  background: rgb(230, 76, 76);
}

.modal-message {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  text-align: center;
  font-size: 18px;
  color: #fff;
  padding: 10px 20px 20px 20px;
  background: transparent;
}

.modal-message p {
  margin: 5px 0;
}

.agreement-section {
  margin: 15px 0;
  text-align: left;
  width: 90%;
  max-width: 470px;
}

.legal {
  display: flex;
  align-items: flex-start;
  margin-bottom: 8px;
}

.legal input[type='checkbox'] {
  margin-right: 8px;
  margin-top: 2px;
  transform: scale(1.1);
}

.legal label {
  color: #fff;
  font-size: 14px;
  line-height: 1.4;
  cursor: pointer;
  font-weight: 500;
}

.legal-text {
  font-size: 12px;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
  padding-left: 20px;
  font-weight: 400;
}

.legal-text :deep(a) {
  color: #ff9eff;
  text-decoration: underline;
  font-weight: 500;
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;
}

.legal-text :deep(a:hover) {
  color: #fff;
  text-shadow: 0 0 4px rgba(255, 158, 255, 0.5);
  text-decoration: underline;
  text-decoration-thickness: 1px;
}

.pay_btn.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: #666;
}

.pay_btn.disabled:hover {
  background-color: #666;
}

@media (max-width: 991px) {
  .modal-content {
    width: 95%;
    max-height: 70vh;
  }

  .modal-iframe {
    height: 350px;
    width: 90%;
    min-width: 300px;
    min-height: 300px;
  }

  .modal-message {
    padding: 8px 15px 15px 15px;
  }

  .agreement-section {
    width: 85%;
  }

  .modal-price {
    font-size: 20px;
    padding: 20px 0 6px 0;
  }

  .close-button {
    font-size: 14px;
    padding: 4px 8px;
  }

  .pay_btn {
    font-size: 16px;
    padding: 4px 8px;
  }

  .modal-message {
    font-size: 16px;
  }

  .legal label {
    font-size: 13px;
  }

  .legal-text {
    font-size: 11px;
  }
}

@media (max-width: 576px) {
  .modal-content {
    width: 98%;
    max-height: 80vh;
  }

  .modal-iframe {
    height: 400px;
    width: 90%;
    min-width: 280px;
    min-height: 220px;
  }

  .modal-message {
    padding: 5px 10px 10px 10px;
  }

  .legal-text {
    font-size: 10px;
    padding-left: 15px;
  }

  .agreement-section {
    width: 90%;
  }

  .modal-price {
    font-size: 18px;
    padding: 16px 0 4px 0;
  }

  .close-button {
    font-size: 12px;
    padding: 3px 6px;
    top: 8px;
    right: 8px;
  }

  .pay_btn {
    font-size: 14px;
    padding: 3px 6px;
  }

  .modal-message {
    font-size: 14px;
  }

  .legal label {
    font-size: 12px;
  }

  .legal input[type='checkbox'] {
    transform: scale(1);
  }
}

@media (max-width: 400px) {
  .modal-iframe {
    min-width: 350px;
    min-height: 350px;
  }

  .modal-price {
    font-size: 16px;
  }

  .close-button {
    font-size: 11px;
    padding: 2px 5px;
  }

  .pay_btn {
    font-size: 12px;
    padding: 2px 5px;
  }

  .modal-message {
    font-size: 12px;
  }

  .legal label {
    font-size: 11px;
  }

  .legal-text {
    font-size: 9px;
  }
}
</style>
